<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Shopee · Repack"
    title="Add Parcels to Carton"
    [subtitle]="'Select parcels to add to carton ' + targetCartonNo"
  />

  <!-- Loading -->
  @if (isLoading || isLoadingParcels) {
    <div class="flex flex-col items-center justify-center py-16 gap-3">
      <div class="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
      <p class="text-sm text-muted-foreground">
        {{ isLoading ? 'Loading carton information…' : 'Loading available parcels…' }}
      </p>
    </div>
  }

  <!-- Feedback alerts -->
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }
  @if (successMessage) {
    <app-inline-alert tone="success" [message]="successMessage" />
  }

  @if (!isLoading && !isLoadingParcels) {

    <!-- Target Carton Info Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Target Carton Information</h2>
      </div>
      <div class="p-4 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
        <div>
          <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Carton No</p>
          <p class="font-mono font-medium text-sm text-foreground">{{ targetCartonNo }}</p>
        </div>
        <div>
          <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Shopee Order No</p>
          <p class="font-mono text-sm text-foreground">{{ targetShopeeNo || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Service Code</p>
          <p class="text-sm text-foreground">{{ targetServiceCode || 'N/A' }}</p>
        </div>
      </div>
    </div>

    <!-- Available Parcels Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
        <div class="flex items-center gap-2 flex-1">
          <h2 class="text-sm font-semibold text-foreground">Available Parcels</h2>
          <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-surface-raised text-muted-foreground border border-border">
            {{ filteredParcels.length }}
          </span>
          @if (selectedParcels.length > 0) {
            <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30">
              {{ selectedParcels.length }} selected
            </span>
          }
        </div>
        <div class="flex items-center gap-2">
          <div class="relative">
            <ng-icon hlm name="lucideSearch" class="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
            <input type="text" placeholder="Search parcels…"
              [(ngModel)]="searchTerm" (input)="filterParcels()"
              class="h-8 border border-border-strong bg-surface rounded-md pl-8 pr-3 text-xs focus-visible:ring-2 focus-visible:ring-accent outline-none w-48" />
          </div>
          @if (searchTerm) {
            <button hlmBtn variant="ghost" size="sm" class="h-8 w-8 p-0"
              (click)="searchTerm = ''; filterParcels()">
              <ng-icon hlm name="lucideX" class="size-3.5" />
            </button>
          }
        </div>
      </div>

      @if (filteredParcels.length === 0) {
        <div class="flex flex-col items-center justify-center py-14 gap-3">
          <ng-icon hlm name="lucidePackage" class="size-10 text-border" />
          <p class="text-sm text-muted-foreground text-center max-w-xs">
            {{ availableParcels.length === 0
              ? 'No available parcels found. You must cancel a carton to make parcels available.'
              : 'No parcels match your search criteria.' }}
          </p>
        </div>
      }

      @if (filteredParcels.length > 0) {
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="text-left px-4 py-2.5 font-medium w-11"></th>
                <th class="text-left px-4 py-2.5 font-medium">Tracking Number</th>
                <th class="text-left px-4 py-2.5 font-medium">Original Shopee Order</th>
                <th class="text-left px-4 py-2.5 font-medium">Weight</th>
                <th class="text-left px-4 py-2.5 font-medium">Dimensions</th>
              </tr>
            </thead>
            <tbody>
              @for (parcel of filteredParcels; track parcel.trackingNumber || $index) {
                <tr class="h-10 border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-surface-raised"
                  [class.bg-blue-50]="isSelected(parcel)"
                  (click)="toggleParcelSelection(parcel)">
                  <td class="px-4 text-center">
                    <input type="checkbox" class="form-check-input w-4 h-4"
                      [checked]="isSelected(parcel)"
                      (click)="$event.stopPropagation()"
                      (change)="toggleParcelSelection(parcel)" />
                  </td>
                  <td class="px-4 font-mono text-[12.5px] text-foreground">{{ parcel.trackingNumber }}</td>
                  <td class="px-4 text-muted-foreground text-xs">{{ parcel.shopeeOrderNo || 'N/A' }}</td>
                  <td class="px-4 text-muted-foreground text-xs">{{ parcel.weight || 'N/A' }} kg</td>
                  <td class="px-4 text-muted-foreground text-xs">
                    {{ parcel.length || '—' }} × {{ parcel.width || '—' }} × {{ parcel.height || '—' }} cm
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- Sticky footer action bar -->
    <div class="sticky bottom-0 bg-background border-t border-border flex items-center justify-between gap-2 px-4 py-3">
      <button type="button" hlmBtn variant="outline" size="sm" (click)="goBack()">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
        Back
      </button>
      <button type="button" hlmBtn size="sm"
        [disabled]="selectedParcels.length === 0 || isLoading"
        (click)="onSubmit()">
        @if (isLoading) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        }
        Add Selected Parcels ({{ selectedParcels.length }})
      </button>
    </div>

  }
</div>
